// Loads the snapshot (catalyst-data.js) from its build-time .gz (snapshot-gzip.plugin.ts),
// decompressing in the browser, and reports real download progress as bytes arrive.
//
// Why the gzip check: if the server (or a future CloudFront setting) sends the file with
// `Content-Encoding: gzip`, the browser has ALREADY decompressed it before we see a byte —
// decompressing again would fail. So we look at the first two bytes (gzip always starts
// 1f 8b) and only decompress when they're there. The progress total follows the same
// check: the .gz size when we're counting compressed bytes, the raw size otherwise.

declare const __SNAPSHOT_GZ__: { url: string; gzBytes: number; rawBytes: number } | null;

export type SnapshotMeta = NonNullable<typeof __SNAPSHOT_GZ__>;

/** The build's snapshot info. Throws when the build had no snapshot (e.g. a deploy that
 *  skipped the sync) — failing loudly beats a page that can never finish loading. */
export function snapshotMeta(meta: SnapshotMeta | null = __SNAPSHOT_GZ__): SnapshotMeta {
  if (!meta) throw new Error("No snapshot in this build — was public/js/catalyst-data.js present at build time?");
  return meta;
}

export function isGzip(bytes: Uint8Array): boolean {
  return bytes.length >= 2 && bytes[0] === 0x1f && bytes[1] === 0x8b;
}

export interface SnapshotHooks {
  /** Per network chunk: bytes received so far, out of the expected total. */
  onProgress: (loaded: number, total: number) => void;
  /** Once the last byte is in (unpacking may still be finishing). */
  onDownloaded?: () => void;
  /** Per unpacked chunk of script text, as it comes out of the decompressor — lets the
   *  loading screen show real data scrolling past while the rest is still downloading. */
  onUnpacked?: (chunk: Uint8Array) => void;
}

/** Streams `res`'s body through `hooks` and returns the snapshot's JavaScript as a Blob
 *  (decompressed if it arrived gzipped). A Blob, not a string, so the ~70MB of script
 *  text isn't copied into the JS heap before it runs. */
export async function readSnapshot(res: Response, meta: SnapshotMeta, hooks: SnapshotHooks): Promise<Blob> {
  const { onProgress, onDownloaded, onUnpacked } = hooks;
  if (!res.ok || !res.body) throw new Error(`Snapshot request failed: ${res.status} ${res.url}`);
  const reader = res.body.getReader();

  // Read until we have the 2 bytes that tell us the format (a chunk can be tiny).
  const head: Uint8Array[] = [];
  let headLen = 0;
  while (headLen < 2) {
    const { done, value } = await reader.read();
    if (done) break;
    head.push(value);
    headLen += value.length;
  }
  const first = new Uint8Array(headLen);
  let at = 0;
  for (const c of head) {
    first.set(c, at);
    at += c.length;
  }
  const gzipped = isGzip(first);
  const total = gzipped ? meta.gzBytes : meta.rawBytes;

  let loaded = first.length;
  onProgress(loaded, total);
  const counted = new ReadableStream<Uint8Array>({
    start(controller) {
      if (first.length) controller.enqueue(first);
    },
    async pull(controller) {
      const { done, value } = await reader.read();
      if (done) {
        onDownloaded?.();
        return controller.close();
      }
      loaded += value.length;
      onProgress(loaded, total);
      controller.enqueue(value);
    },
    cancel(reason) {
      return reader.cancel(reason);
    },
  });

  // The cast: TS's DOM lib types DecompressionStream's writable side as BufferSource,
  // which a Uint8Array stream is — it just doesn't narrow through pipeThrough.
  const unpacked = gzipped
    ? counted.pipeThrough(new DecompressionStream("gzip") as unknown as ReadableWritablePair<Uint8Array, Uint8Array>)
    : counted;
  const body = onUnpacked
    ? unpacked.pipeThrough(
        new TransformStream<Uint8Array, Uint8Array>({
          transform(chunk, controller) {
            onUnpacked(chunk);
            controller.enqueue(chunk);
          },
        }),
      )
    : unpacked;
  const blob = await new Response(body).blob();
  return new Blob([blob], { type: "text/javascript" });
}
